import React, { useEffect, useMemo } from "react";
import IconDownload from "../iconComponents/IconDownload";

const PREVIEWABLE_IMAGE_EXT = /\.(jpe?g|png|gif|webp)$/i;
const PDF_EXT = /\.pdf$/i;

function isPdfFile(file) {
    return file.type === "application/pdf" || PDF_EXT.test(file.name || "");
}

function isPreviewableImageFile(file) {
    if (file.type && file.type.startsWith("image/")) {
        // svg can carry a <script>, and browsers can render tiff inconsistently
        // (or not at all) via <img> -- neither is treated as previewable here.
        return file.type !== "image/svg+xml" && file.type !== "image/tiff";
    }
    return PREVIEWABLE_IMAGE_EXT.test(file.name || "");
}

// Renders a preview for a file about to be attached (a browser File object,
// not yet uploaded), or a filename + download link for one already saved to
// the server -- see GitHub #640. Deliberately narrow about what's "safe to
// embed": only PDF (<iframe>) and common raster images (<img>) are ever
// rendered inline, even though other file types may be allowed uploads,
// since embedding an untrusted SVG/HTML file via <iframe>/<object> can
// execute script (see GitHub #650).
export default function AttachmentPreview({ file, attachment, onDownload }) {

    const blobUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
    useEffect(() => {
        return () => { blobUrl && URL.revokeObjectURL(blobUrl); };
    }, [blobUrl]);

    if (attachment) {
        return (
            <div className="attachment-preview gap-below-s">
                <a onClick={() => onDownload(attachment.id, attachment.file_name)}>
                    <IconDownload className="iblock" height="13px" pathClass="p-fill" /> &nbsp;
                </a>
                <span className="content1 light-black iblock">{attachment.file_name}</span>
                <span className="content2 light g7-text gap-left-xs iblock">(already saved -- download to view)</span>
            </div>
        );
    }

    if (!file) return null;

    if (isPdfFile(file)) {
        return (
            <div className="attachment-preview gap-below-s">
                <p className="content2 light-black gap-below-xs">{file.name}</p>
                <iframe
                    src={blobUrl}
                    title={file.name}
                    style={{ width: "100%", height: "320px", border: "1px solid #DFDFE2", borderRadius: "4px" }}
                />
            </div>
        );
    }

    if (isPreviewableImageFile(file)) {
        return (
            <div className="attachment-preview gap-below-s">
                <p className="content2 light-black gap-below-xs">{file.name}</p>
                <img
                    src={blobUrl}
                    alt={file.name}
                    style={{ maxWidth: "100%", maxHeight: "320px", border: "1px solid #DFDFE2", borderRadius: "4px" }}
                />
            </div>
        );
    }

    return (
        <div className="attachment-preview gap-below-s">
            <p className="content1 light-black">
                {file.name} <span className="content2 light g7-text">({Math.round(file.size / 1024)} KB)</span>
            </p>
            <a href={blobUrl} target="_blank" rel="noreferrer" className="content2 link1">
                Can't preview this file type -- open it to double check before sending
            </a>
        </div>
    );
}
